import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import { useEffect, forwardRef } from "react";
import { BaseChartColors } from "../constants";
import { configureNumberFormatter } from "../utils";
import { BaseDateChartData } from "./types";
import {
  configureChartData,
  configureLineColors,
  createDateAxis,
  createLegends,
  createSeriesConfigs,
  createValueAxis,
} from "./utils";

interface BaseDateDataChartProps {
  id: string;
  chartData: BaseDateChartData[];
  baseColor?: BaseChartColors | string[];
  inputDateFormat?: string;
  showPercentageSymbol?: boolean;
  yAxisLabel?: string;
  showCustomTooltip?: boolean;
}

export const BaseDateDataChart = forwardRef<
  am4charts.XYChart,
  BaseDateDataChartProps
>((props, ref) => {
  const {
    id,
    chartData,
    baseColor,
    inputDateFormat,
    showPercentageSymbol,
    yAxisLabel,
    showCustomTooltip,
  } = props;

  useEffect(() => {
    const chart = am4core.create(id, am4charts.XYChart);
    if (typeof ref === "function") {
      ref(chart);
    } else if (ref) {
      ref.current = chart;
    }
    chart.paddingTop = 20;
    chart.paddingLeft = 0;
    chart.paddingRight = 64;
    chart.paddingBottom = 0;

    configureChartData(chart, chartData);

    const seriesConfigs = createSeriesConfigs(chartData);

    chart.dateFormatter.inputDateFormat = inputDateFormat || "yyyy-MM-dd";
    chart.maskBullets = false;

    configureNumberFormatter(chart);
    createDateAxis(chart);
    createValueAxis(chart, !!showPercentageSymbol, yAxisLabel);
    configureLineColors(
      chart,
      seriesConfigs,
      chartData,
      baseColor,
      showCustomTooltip
    );
    createLegends(chart);

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "none";

    chart.cursor.lineX.disabled = true;
    chart.cursor.lineY.disabled = true;

    return () => {
      chart.dispose();
    };
  }, [
    id,
    chartData,
    baseColor,
    inputDateFormat,
    showPercentageSymbol,
    yAxisLabel,
    showCustomTooltip,
    ref,
  ]);

  return <div id={props.id} style={{ width: "100%", height: "100%" }}></div>;
});
